<template>
  <div class="col-span-12">
    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-4 mt-4">
        <button class="btn-primary bg-primary px-2 py-1 text-white text-sm rounded-sm" @click="sendDocument('REGISTRATION_CERTIFICATE')">
          <i class="fa fa-reply mx-1"></i> Resend Certificate <span class="lds-ring">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </span>
        </button>
      </div>
    </div>
    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-4 mt-1">
        <table class="w-full leading-normal text-gray" style="border: solid 1px #dedede;">
          <thead class="bg-gray-pale text-xs">
          <tr class="bg-gray-pale">
            <th class="px-2 py-1">#</th>
            <th class="px-2 py-1">Document</th>
            <th class="px-2 py-1 text-center">EDMS</th>
            <th class="px-2 py-1 text-center">Date</th>
          </tr>
          </thead>
          <tbody>
            <tr>
              <td class="px-2 py-1" colspan="12">
                <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent form-text text-xs" v-model="search" v-on:change="fetch" placeholder="Search Document Name" />
                <div class="mx-auto">
                  <span class="lds-ring" v-if="loading">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                  </span>
                </div>
              </td>
            </tr>
            <tr style="border: solid 1px #dedede;" v-for="(document, idx) in documents"
                class="cursor-pointer text-gray hover:underline hover:text-blue hover:font-bold"
                :key="idx"
                @click="view(document)">
              <td class="px-2 py-1">
                 <span class="text-xs">
                   {{(currentPage - 1) * limit + idx + 1}}
                 </span>
              </td>
              <td class="px-2 py-1">
                 <span class="text-xs text-capitalize">
                   {{cleanEntityType(document.name.toLowerCase())}}
                 </span>
              </td>
              <td class="px-2 py-1 text-center text-xs">
                <i v-if="document.edmsId" class="text-green fa fa-check-double">Yes</i>
                <i v-else class="text-gray-300 fa fa-check">No</i>
              </td>
              <td class="px-2 py-1 text-xs text-center">
                <span>{{dateFormat(document.createdAt)}}</span>
              </td>
            </tr>
            <tr v-if="documents.length === 0">
              <td colspan="12" class="bg-inertgrey text-center py-1">
                <span class="text-xs">No results found</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="grid grid-cols-12 gap-4 mx-1" v-if="pagination.totalPages> 1">
          <div class="col-span-2">
            <button class="btn-primary" @click="previous" :class="(currentPage > 1) ? 'paginate-btn' :'inert-btn'">Previous</button>
          </div>
          <div class="col-span-2">
            <div class="grid grid-cols-12 gap-4">
              <div class="col-span-12">
                <p class="paginate-details text-center">
                  <span class="bg-gray text-white px-2 py-1 rounded-sm text-xs bg-primary text-white">
                    {{(currentPage)}}/{{pagination.totalPages}}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div class="col-span-2">
            <button class="btn-primary" @click="next"
                    :class="(currentPage < pagination.totalPages) ? 'paginate-btn' :'inert-btn'">Next
            </button>
          </div>
          <div class="col-span-2"></div>
          <div class="col-span-4">
            <div class="grid grid-cols-12 gap-4">
              <div class="col-span-12 mx-auto">
                <p class="paginate-details text-center text-xs font-bold">
                  <span>{{((currentPage - 1) * limit)}}</span> -
                  <span v-if="limit > documents.length ">{{pagination.totalElements}}</span>
                  <span v-else>{{currentPage * limit}}</span>
                  of {{pagination.totalElements}}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-span-8 mt-1">
        <div class="relative">
          <div v-if="loadingDocument" class="absolute inset-0 bg-gray-50 bg-opacity-75 flex items-center justify-center rounded z-50">
            <div class="text-center">
              <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
              <p class="mt-2 text-sm text-gray-600">Loading...</p>
            </div>
          </div>
          <div class="grid grid-cols-12 gap-4">
            <div class="col-span-12">
              <EDMSDocument class="w-full border border-gray" ref="edmsFile" :file="getEDMSFile"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useBusinessRegistryStore } from "@/stores/business-registry";
import { useBusinessFileStore } from "@/stores/business-file";
import { useEntityDocumentsStore } from "@/stores/entity-documents";
import { useNetworkStore } from "@/stores/network";
import { dateFormat } from "@/commons/composables/utilities";
import { useToast } from "@/composables";
import EDMSDocument from "@/domains/home/services/registry/business/document/EDMSDocument.vue";

const props = defineProps<{ brn: string }>();

const registryStore = useBusinessRegistryStore();
const fileStore = useBusinessFileStore();
const documentsStore = useEntityDocumentsStore();
const networkStore = useNetworkStore();
const { successToast, errorToast, warningToast } = useToast();

const edmsFile = ref<InstanceType<typeof EDMSDocument> | null>(null);
const loading = ref(false);
const loadingDocument = ref(false);
const resending = ref(false);
const currentPage = ref(1);
const limit = ref(10);
const documents = ref<any[]>([]);
const pagination = ref({ totalElements: 0, totalPages: 0 });
const search = ref("");

const getEDMSFile = computed(() => registryStore.edms_file);

onMounted(() => {
  fetch();
});

const fetch = () => {
  loading.value = true;
  let data = {
    brn: props.brn,
    limit: limit.value,
    page: currentPage.value,
    search: search.value
  };
  documentsStore.fetchCompanyDocuments(data);
};

const cleanEntityType = (name: string) => {
  let n = name.replaceAll("_", " ");
  return n.charAt(0).toUpperCase() + n.slice(1);
};

const view = (document: any) => {
  if (document.edmsId) {
    let data = { documentHandle: document.edmsId, overlay: "" };
    loadingDocument.value = true;
    registryStore.fetchCompanyEDMSFile(data);
  } else {
    errorToast("Document Error", "This document is not yet in EDMS");
  }
};

const sendDocument = (name: string) => {
  let data = { type: name, brn: props.brn };
  resending.value = true;
  fileStore.sendCompanyDocumentToClient(data);
};

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    fetch();
  }
};

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetch();
  } else {
    warningToast("Observation", "No more data to show");
  }
};

watch(() => documentsStore.company_documents, (data) => {
  if (data) {
    loading.value = false;
    documents.value = data.content;
    pagination.value = {
      totalPages: data.totalPages,
      totalElements: data.totalElements
    }
  }
})

watch(() => registryStore.edms_file, () => {
  loadingDocument.value = false;
});

watch(() => registryStore.edms_error, () => {
  loadingDocument.value = false;
  errorToast("Error", "Failed to get document");
});

watch(() => networkStore.network_status, () => {
  loadingDocument.value = false;
  errorToast("Document Error", "Document loading times out");
});

watch(() => fileStore.send_document, () => {
  resending.value = false;
  successToast("Success", "Document resent");
});

watch(() => fileStore.send_document_error, () => {
  resending.value = false;
  errorToast("Error", "Failed to resend document");
});
</script>

